import { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from '@/shared/lib';

/** 로고가 떠오른 뒤 막이 걷히기 시작할 때까지(ms). */
const LOGO_HOLD_MS = 1300;
/** 막이 위로 걷히는 시간(ms) — 걷힌 뒤 막을 내린다. site-header-intro 의 전환 시간과 같다. */
const REVEAL_MS = 900;
/** 같은 브라우저 세션에서는 한 번만 보여 준다. */
const SEEN_KEY = 'superstructure:home-intro-seen';

export type HeaderIntroPhase = 'logo' | 'reveal' | 'done';

const hasSeenIntro = (): boolean => {
  try {
    return window.sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    // 저장소가 막힌 환경(사생활 보호 모드 등)에서는 매번 보여 주지 않는 쪽이 안전하다.
    return true;
  }
};

const markIntroSeen = (): void => {
  try {
    window.sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    // 기록하지 못해도 화면 동작에는 영향이 없다.
  }
};

/**
 * HOME 첫 진입 연출 단계 — 흑연 막 위에 로고가 떠오르고(logo), 막이 위로 걷히고(reveal), 막을 내린다(done).
 * 세션당 한 번만, 그 전에 스크롤하거나 누르면 바로 끝낸다. 동작 줄이기이거나 이미 내려간 위치로 열리면 건너뛴다.
 */
export const useHeaderIntro = (enabled: boolean): HeaderIntroPhase => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [phase, setPhase] = useState<HeaderIntroPhase>(() =>
    enabled && !prefersReducedMotion && window.scrollY === 0 && !hasSeenIntro() ? 'logo' : 'done',
  );

  useEffect(() => {
    if (phase === 'done') return undefined;
    markIntroSeen();

    const timer = setTimeout(
      () => setPhase(phase === 'logo' ? 'reveal' : 'done'),
      phase === 'logo' ? LOGO_HOLD_MS : REVEAL_MS,
    );
    const skip = (): void => setPhase('done');
    window.addEventListener('scroll', skip, { passive: true });
    window.addEventListener('pointerdown', skip);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', skip);
      window.removeEventListener('pointerdown', skip);
    };
  }, [phase]);

  return phase;
};
